/**
 * Snoeff cart drawer behaviour (Horizon 4.x).
 *
 * - Add-to-cart requests that don't come from Horizon's own product form (the TrueStride PDP's buy box,
 *   multi-dog add, sticky bar) are noticed here: the drawer gets the fresh cart and opens.
 *   Horizon is told through its standard cart:lines-update event, so the header count, the drawer
 *   contents and anything else listening stay in sync.
 * - TrueStride forms (`form.ts-form`): add to cart without leaving the page.
 * - Upsell rows: add with one tap; arrows page through them.
 * - Reserve timer: 15 minutes per browser session, kept on one line.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CART_ADD_JS = /\/cart\/add\.js(\?|$)/;

if (!window.__snoeffCartInit) {
  window.__snoeffCartInit = true;

  const origFetch = window.fetch.bind(window);

  /** Gets the cart and the drawer's HTML, then tells Horizon so the drawer, header count etc. update. */
  async function announce(action, lines, source, sectionsHtml) {
    const deferred = CartLinesUpdateEvent.createPromise();
    (source instanceof Element ? source : document.body).dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise })
    );
    try {
      const [cart, html] = await Promise.all([
        origFetch(ROOT + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } }).then((r) => r.json()),
        sectionsHtml && sectionsHtml[SECTION_ID]
          ? sectionsHtml[SECTION_ID]
          : origFetch(ROOT + '?sections=' + SECTION_ID, { cache: 'no-store' })
              .then((r) => r.json())
              .then((j) => j[SECTION_ID]),
      ]);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: { [SECTION_ID]: html },
          items: cart.items,
          itemCount: cart.item_count,
          source: 'snoeff-cart',
          didError: false,
        },
      });
      return cart;
    } catch (err) {
      deferred.reject(err);
      throw err;
    }
  }

  function openDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }

  /** Lines in the shape Horizon's event expects, from a /cart/add.js response (one item or { items }). */
  function linesFrom(added) {
    const items = added && Array.isArray(added.items) ? added.items : added && added.variant_id ? [added] : [];
    return items.map((i) => ({ merchandiseId: String(i.variant_id), quantity: i.quantity }));
  }

  /** Horizon's own requests ask for section HTML and announce themselves; leave those alone. */
  function asksForSections(init) {
    const body = init && init.body;
    if (body instanceof FormData) return body.has('sections');
    if (typeof body === 'string') {
      try {
        return 'sections' in JSON.parse(body);
      } catch (e) {}
    }
    return false;
  }

  // Watch /cart/add.js requests from anywhere else (the TrueStride buy box, multi-dog add, sticky bar).
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const promise = origFetch(input, init);
    if (CART_ADD_JS.test(url) && !asksForSections(init)) {
      promise
        .then(async (response) => {
          if (!response || !response.ok) return;
          const added = await response.clone().json().catch(() => null);
          await announce('add', linesFrom(added), null, added && added.sections);
          openDrawer();
        })
        .catch((err) => console.error('[snoeff cart]', err));
    }
    return promise;
  };

  // TrueStride forms: add to cart without a page load (the watcher above opens the drawer).
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.classList || !form.classList.contains('ts-form')) return;
    e.preventDefault();

    const atc = form.querySelector('[data-ts-atc]');
    if (atc && atc.dataset.tsBusy) return;
    if (atc) {
      atc.dataset.tsBusy = '1';
      atc.setAttribute('aria-busy', 'true');
    }

    window
      .fetch(ROOT + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
      .then((r) => {
        if (!r.ok) throw new Error('add failed: ' + r.status);
      })
      .catch((err) => {
        console.error('[snoeff cart]', err);
        window.location.href = ROOT + 'cart';
      })
      .finally(() => {
        if (atc) {
          delete atc.dataset.tsBusy;
          atc.removeAttribute('aria-busy');
        }
      });
  });

  // Upsell rows: arrows move one product; at the end they loop back to the first.
  document.addEventListener('click', (e) => {
    const arrow = e.target instanceof Element && e.target.closest('[data-action="snoeff-upsell-nav"]');
    if (!arrow) return;
    e.preventDefault();
    const box = arrow.closest('[data-upsells]');
    const track = box && box.querySelector('[data-upsell-track]');
    if (!track || !track.children.length) return;
    const w = track.clientWidth;
    const count = track.children.length;
    const current = Math.round(track.scrollLeft / w);
    const next = (current + parseInt(arrow.dataset.dir, 10) + count) % count;
    track.scrollTo({ left: next * w });
  });

  // Upsell rows: add one of the product, then refresh the drawer.
  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element && e.target.closest('[data-action="snoeff-upsell-add"]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const response = await origFetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: SECTION_ID }),
      });
      if (!response.ok) throw new Error('cart/add failed: ' + response.status);
      const added = await response.json();
      await announce('add', linesFrom(added), btn, added.sections);
    } catch (err) {
      console.error('[snoeff upsell add]', err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  // Reserve timer: the deadline is kept for the browser session, so it keeps ticking across pages;
  // at 0:00 the block hides (and stays hidden for the session).
  const DURATION_MS = 15 * 60 * 1000;
  const KEY = 'snoeff_reserve_deadline';
  const MIN_PX = 8;

  function getDeadline() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(KEY);
    } catch (e) {}
    let dl = raw ? parseInt(raw, 10) : NaN;
    if (!raw || isNaN(dl)) {
      dl = Date.now() + DURATION_MS;
      try {
        sessionStorage.setItem(KEY, String(dl));
      } catch (e) {}
    }
    return dl;
  }

  function fmt(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  // Keep the timer sentence on one line: start at the CSS size and only shrink when it doesn't fit.
  function fitBanner(banner) {
    if (!banner.clientWidth) return; // drawer closed: fit once it opens (ResizeObserver)
    banner.style.fontSize = '';
    let px = parseFloat(getComputedStyle(banner).fontSize);
    let guard = 0;
    while (banner.scrollWidth > banner.clientWidth + 0.5 && px > MIN_PX && guard++ < 40) {
      px -= 0.25;
      banner.style.fontSize = px + 'px';
    }
  }
  const fitObserver =
    'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((e) => fitBanner(e.target))) : null;

  function tick() {
    // Look the banner up every tick: the drawer HTML is morphed in on cart updates.
    const banner = document.querySelector('[data-reserve-banner]');
    if (!banner) return;
    if (!banner.__tsFit) {
      banner.__tsFit = true;
      if (fitObserver) fitObserver.observe(banner);
      fitBanner(banner);
    }
    const remaining = getDeadline() - Date.now();
    if (remaining <= 0) {
      banner.style.display = 'none';
      return;
    }
    banner.style.display = '';
    const el = banner.querySelector('[data-reserve-timer]');
    if (el) el.textContent = fmt(remaining);
  }

  tick();
  setInterval(tick, 1000);
}
